import { Info, LoaderCircle, type LucideIcon, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/utils';

type Tone = 'loading' | 'error' | 'info';

const TONES: Record<Tone, { icon: LucideIcon; iconClass: string }> = {
  loading: { icon: LoaderCircle, iconClass: 'animate-spin text-accent' },
  error: { icon: TriangleAlert, iconClass: 'text-danger' },
  info: { icon: Info, iconClass: 'text-fg-muted' },
};

export interface StatusScreenProps {
  tone: Tone;
  title: string;
  description?: string;
  /** Extra lines rendered in a monospace block (e.g. config validation issues). */
  details?: readonly string[];
  action?: ReactNode;
}

/** Full-viewport message used while booting, authenticating, or when startup fails. */
export function StatusScreen({ tone, title, description, details, action }: StatusScreenProps) {
  const { icon: Icon, iconClass } = TONES[tone];
  return (
    <div className="grid min-h-dvh place-items-center bg-surface-sunken p-6">
      <div
        role={tone === 'error' ? 'alert' : 'status'}
        className="w-full max-w-md rounded-lg border border-border bg-surface-raised p-8 text-center shadow-xs"
      >
        <Icon className={cn('mx-auto size-8', iconClass)} aria-hidden="true" />
        <h1 className="mt-4 font-semibold text-lg text-fg">{title}</h1>
        {description ? <p className="mt-2 text-fg-muted text-sm">{description}</p> : null}
        {details && details.length > 0 ? (
          <pre className="mt-4 overflow-x-auto rounded-md bg-surface-sunken p-3 text-left font-mono text-fg-muted text-xs">
            {details.join('\n')}
          </pre>
        ) : null}
        {action ? <div className="mt-6 flex justify-center gap-2">{action}</div> : null}
      </div>
    </div>
  );
}
